import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { InlineLoading } from "./LoadingOverlay";
import { SearchBox } from "@/search/SearchBox";
import { Plus } from "lucide-react";
import { useSearchIcd10Codes } from "@/clinical-codes/useSearchIcd10Codes";
import { useSearchOpcs4Codes } from "@/clinical-codes/useSearchOpcs4Codes";
import type { ClinicalCode, CodeType } from "@/clinical-codes/types/ClinicalCodeTypes";
import type { OpcsVersion } from "@/clinical-codes/opcsVersion";
import { getOpcsVersionLabel } from "@/clinical-codes/opcsVersion";

type AddClinicalCodeDialogProps = {
  onSelect: (code: ClinicalCode & { type: CodeType }) => void;
  triggerSize?: "sm" | "default" | "lg" | "icon";
  opcsVersion?: OpcsVersion;
};

export function AddClinicalCodeDialog({ onSelect, triggerSize = "sm", opcsVersion }: AddClinicalCodeDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [addingCodeType, setAddingCodeType] = useState<"procedure" | "diagnosis">("procedure");
  const [searchQuery, setSearchQuery] = useState("");

  // Always call hooks, pick results by type
  const icd10 = useSearchIcd10Codes(searchQuery);
  const opcs4 = useSearchOpcs4Codes(searchQuery, undefined, opcsVersion);

  const isLoadingCodes = addingCodeType === "procedure" ? opcs4.isLoading : icd10.isLoading;
  const clinicalCodes: (ClinicalCode & { type: CodeType })[] =
    addingCodeType === "procedure"
      ? (opcs4.codes || []).map((code) => ({ ...code, type: "OPCS-4" as const }))
      : (icd10.codes || []).map((code) => ({ ...code, type: "ICD-10" as const }));

  const handleSelect = (code: ClinicalCode & { type: CodeType }) => {
    onSelect(code);
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size={triggerSize} variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Add
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Clinical Code</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={addingCodeType === "procedure" ? "default" : "outline"}
              onClick={() => setAddingCodeType("procedure")}
              size="sm"
            >
              {opcsVersion ? getOpcsVersionLabel(opcsVersion) : "OPCS-4"}
            </Button>
            <Button
              variant={addingCodeType === "diagnosis" ? "default" : "outline"}
              onClick={() => setAddingCodeType("diagnosis")}
              size="sm"
            >
              ICD-10
            </Button>
          </div>

          <SearchBox searchQuery={searchQuery} onSearchChange={setSearchQuery} />

          <div className="max-h-60 overflow-y-auto">
            {isLoadingCodes ? (
              <InlineLoading message="Searching codes..." />
            ) : (
              <div className="space-y-1">
                {clinicalCodes?.map((code) => (
                  <div
                    key={code.id}
                    className="p-2 border rounded cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => handleSelect(code)}
                  >
                    <div className="font-mono text-sm">{code.code}</div>
                    <div className="text-xs text-gray-600">{code.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddClinicalCodeDialog;
